import { useEffect } from "react";
import { useQueryClient, type QueryClient } from "@tanstack/react-query";
import { useAuthStore } from "@/store/auth";
import {
  acquireSubscription,
  type SubscriptionHandle,
} from "@/features/chat/api/realtime";
import type { Tender, TenderActionStatus } from "../types";
import type { ZETender } from "@/features/ze_tenders/types";

/** Wspólny kanał Centrifugo, na którym backend ogłasza każdą zmianę ruszającą wiersz
 *  listy przetargów: przejęcie/zwolnienie wyceny (z nazwiskiem), a przez sygnały modeli
 *  także propozycje wyceny, oferty, tury i status przetargu — zob. apps/tenders/realtime.py. */
export const TENDER_CHANGES_CHANNEL = "tenders:changes";

export interface PricingClaimChangedEvent {
  type: "pricing_claim_changed";
  tender_id: string;
  round_id: string;
  /** Status wiersza listy PO zmianie: „pricing_claimed" po przejęciu,
   *  „needs_pricing" po zwolnieniu. */
  action_status: Extract<TenderActionStatus, "pricing_claimed" | "needs_pricing">;
  pricing_claimed_by: string | null;
  pricing_claimed_by_name: string | null;
  pricing_claimed_at: string | null;
  at: string;
}

/** Zwykła zmiana bez stanu w payloadzie — strona dociąga wiersz sama. */
export interface TenderChangedEvent {
  type: "tender_changed";
  tender_id: string;
  kind: "tender" | "round" | "proposal" | "offer";
  deleted: boolean;
  at: string;
}

export type TenderListEvent = PricingClaimChangedEvent | TenderChangedEvent;

/** Ile czekamy z dociągnięciem list po serii zdarzeń — chip podmieniamy od razu z
 *  payloadu, refetch w tle tylko potwierdza (liczniki chipów, inne kolumny). */
const REFETCH_DEBOUNCE_MS = 500;

function isClaimEvent(data: unknown): data is PricingClaimChangedEvent {
  const e = data as Partial<PricingClaimChangedEvent> | null;
  return (
    !!e &&
    e.type === "pricing_claim_changed" &&
    typeof e.tender_id === "string" &&
    (e.action_status === "pricing_claimed" || e.action_status === "needs_pricing")
  );
}

function isTenderChangedEvent(data: unknown): data is TenderChangedEvent {
  const e = data as Partial<TenderChangedEvent> | null;
  return !!e && e.type === "tender_changed" && typeof e.tender_id === "string";
}

type ClaimRow = Pick<Tender, "id" | "action_status" | "pricing_claimed_by" | "pricing_claimed_by_name">;

function patchRows<T extends ClaimRow>(rows: T[] | undefined, event: PricingClaimChangedEvent): T[] | undefined {
  if (!Array.isArray(rows)) return rows;
  let touched = false;
  const next = rows.map((row) => {
    if (row.id !== event.tender_id) return row;
    touched = true;
    return {
      ...row,
      action_status: event.action_status,
      pricing_claimed_by: event.pricing_claimed_by,
      pricing_claimed_by_name: event.pricing_claimed_by_name,
    };
  });
  return touched ? next : rows;
}

/**
 * Podmienia chip „Przejęte (…)" / „Czeka na wyliczenie wyceny" w KAŻDEJ zbuforowanej
 * liście przetargów (główna lista z dowolnym filtrem statusu + portfel ZE) — bez HTTP,
 * wprost z payloadu zdarzenia. Zwraca true, gdy któryś wiersz faktycznie się zmienił.
 */
export function applyPricingClaimEvent(qc: QueryClient, event: PricingClaimChangedEvent): boolean {
  let touched = false;
  // Lista główna: ["tenders", params?]. Pod tym samym prefiksem żyją też detale
  // (["tenders", id]) i liczniki — to nie tablice, więc zostają nietknięte.
  qc.setQueriesData<unknown>({ queryKey: ["tenders"] }, (old: unknown) => {
    if (!Array.isArray(old)) return old;
    const next = patchRows(old as Tender[], event);
    if (next !== old) touched = true;
    return next;
  });
  qc.setQueriesData<ZETender[]>({ queryKey: ["zt", "tenders"] }, (old) => {
    const next = patchRows(old, event);
    if (next !== old) touched = true;
    return next;
  });
  return touched;
}

/** Dociągnij w tle wszystko, co pokazują listy: wiersze, liczniki chipów, detale. */
export function invalidateAfterTenderEvents(qc: QueryClient, tenderIds: Iterable<string>) {
  void qc.invalidateQueries({ queryKey: ["tenders"] });
  void qc.invalidateQueries({ queryKey: ["zt", "tenders"] });
  void qc.invalidateQueries({ queryKey: ["zt", "action-counts"] });
  for (const id of tenderIds) {
    void qc.invalidateQueries({ queryKey: ["zt", "tender", id] });
  }
}

/**
 * „Na żywo" dla list przetargów. Montowane WYŁĄCZNIE na stronach z listą (Przetargi,
 * Przetargi ZE): subskrypcja kanału `tenders:changes` żyje tylko póki strona jest
 * otwarta — po zejściu z niej nic nie pobiera się w tle. Przejęcie od razu podmienia
 * wiersz w cache (chip zmienia się natychmiast); każde zdarzenie — przejęcie,
 * propozycja wyceny, oferta, nowa tura, zmiana statusu, usunięcie — po chwili dociąga
 * listy i liczniki w tle, żeby wszystkie kolumny były świeże.
 *
 * Bez Centrifugo (brak połączenia, brak uprawnień do kanału) hook po prostu nic nie
 * robi — lista zachowuje się jak dotąd (odświeżenie ręczne / po powrocie do karty).
 */
export function useTenderListLive(enabled = true) {
  const qc = useQueryClient();
  const userId = useAuthStore((s) => s.user?.id);

  useEffect(() => {
    if (!enabled || !userId) return;
    let cancelled = false;
    let handle: SubscriptionHandle | null = null;
    let handler: ((ctx: { data: unknown }) => void) | null = null;
    let debounce: ReturnType<typeof setTimeout> | null = null;
    const pending = new Set<string>();

    (async () => {
      try {
        const h = await acquireSubscription(TENDER_CHANGES_CHANNEL);
        if (cancelled) {
          h.release();
          return;
        }
        handle = h;
        handler = (ctx) => {
          const data = ctx.data;
          if (isClaimEvent(data)) {
            applyPricingClaimEvent(qc, data);
          } else if (!isTenderChangedEvent(data)) {
            return;
          }
          pending.add(data.tender_id);
          if (debounce) clearTimeout(debounce);
          debounce = setTimeout(() => {
            debounce = null;
            const ids = [...pending];
            pending.clear();
            invalidateAfterTenderEvents(qc, ids);
          }, REFETCH_DEBOUNCE_MS);
        };
        h.sub.on("publication", handler);
      } catch (err) {
        console.warn("[tenders] tenders:changes subscription failed", err);
      }
    })();

    return () => {
      cancelled = true;
      if (debounce) clearTimeout(debounce);
      try {
        if (handler) handle?.sub.off("publication", handler);
        handle?.release();
      } catch {
        /* ignore teardown errors */
      }
    };
  }, [enabled, userId, qc]);
}
